<template>
  <el-dialog
    v-model="innerOpen"
    width="920px"
    append-to-body
    :close-on-click-modal="false"
    :lock-scroll="false"
    class="sd-dialog"
    :show-close="true"
    :before-close="handleMainBeforeClose"
    @closed="onDialogClosed"
  >
    <div class="sd-root">
      <div class="sd-header">
        <div class="sd-avatar">
          <span>{{ initials }}</span>
        </div>
        <div class="sd-header-text">
          <div class="sd-name-row">
            <span class="sd-name">{{ holderName }}</span>
            <span
              v-if="showControlHeader"
              class="sd-core-pill"
              :class="{ 'sd-core-pill--clickable': canEditRelation }"
              role="button"
              tabindex="0"
              @click="onAssocPillClick"
              @keydown.enter.prevent="onAssocPillClick"
            >
              已关联核心股东：{{ localCoreName || coreName }}
              <span :class="'ctrl-type-tag ctrl-type-' + (localRelationType || 'direct')">{{ ctrlTypeLabel }}</span>
            </span>
            <span
              v-else-if="showSuspiciousHeader"
              class="sd-suspicious-pill"
              :class="{ 'sd-core-pill--clickable': canEditRelation }"
              role="button"
              tabindex="0"
              @click="onAssocPillClick"
              @keydown.enter.prevent="onAssocPillClick"
            >
              疑似关联核心股东：{{ localCoreName || coreName || suspiciousRow?.core_name }}
            </span>
            <span v-else class="sd-no-link-btns">
              <el-button size="small" type="primary" @click="openDirectConfirmModal">关联核心股东</el-button>
            </span>
          </div>
          <p class="sd-subtitle">股东详细信息概览</p>
        </div>
        <button type="button" class="sd-refresh-btn" @click="handleRefresh" title="刷新数据">↻</button>
      </div>

      <div class="sd-grid">
        <div class="sd-left">
          <section class="sd-card">
            <h3 class="sd-card-title">基础信息</h3>
            <div class="sd-info-list">
              <div class="sd-info-row">
                <span class="sd-label">股东名称</span>
                <span class="sd-value">{{ holderName }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">证券账户</span>
                <span class="sd-value sd-muted">{{ accountCode || '待接入股东唯一表' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">地址</span>
                <span class="sd-value sd-muted">{{ contactAddress || '待接入地址信息' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">联系电话</span>
                <span class="sd-value sd-muted">{{ contactPhone || '待接入联系方式' }}</span>
              </div>
            </div>
          </section>

          <section class="sd-card">
            <h3 class="sd-card-title">持仓信息</h3>
            <div class="sd-info-list">
              <div class="sd-info-row">
                <span class="sd-label">首次出现</span>
                <span class="sd-value sd-muted">{{ firstAppear || '—' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">最近出现</span>
                <span class="sd-value sd-muted">{{ lastAppear || '—' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">持仓期数</span>
                <span class="sd-value sd-muted">{{ holdPeriods ? holdPeriods + ' 期' : '—' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">交易期数</span>
                <span class="sd-value sd-muted">{{ tradePeriods ? tradePeriods + ' 期' : '—' }}</span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">当前持股数</span>
                <span class="sd-value sd-highlight">
                  {{ holdSharesWan != null ? holdSharesWan.toFixed(2) + ' 万股' : '—' }}
                </span>
              </div>
            </div>
          </section>

          <section class="sd-card sd-card-gradient">
            <h3 class="sd-card-title">筹码信息</h3>
            <div class="sd-info-list">
              <div class="sd-info-row sd-info-row--help">
                <span class="sd-label">
                  FIFO 成本
                  <button type="button" class="sd-help-q" title="筹码计算明细" @click.stop="openChipExplain()">?</button>
                </span>
                <span class="sd-value">
                  {{ fifoCost != null ? '¥' + fifoCost.toFixed(2) : '—' }}
                </span>
              </div>
              <div class="sd-info-row sd-info-row--help">
                <span class="sd-label">
                  浮动盈亏
                  <button type="button" class="sd-help-q" title="筹码计算明细" @click.stop="openChipExplain()">?</button>
                </span>
                <span
                  class="sd-value"
                  :class="fifoPnl != null && fifoPnl > 0 ? 'sd-up' : (fifoPnl != null && fifoPnl < 0 ? 'sd-down' : '')"
                >
                  {{ fifoPnl != null ? '¥' + formatNumber(fifoPnl) : '—' }}
                </span>
              </div>
              <div class="sd-info-row sd-info-row--help">
                <span class="sd-label">
                  现金收益
                  <button type="button" class="sd-help-q" title="筹码计算明细" @click.stop="openChipExplain()">?</button>
                </span>
                <span
                  class="sd-value"
                  :class="cashReturn != null && cashReturn > 0 ? 'sd-up' : (cashReturn != null && cashReturn < 0 ? 'sd-down' : '')"
                >
                  {{ cashReturn != null ? '¥' + formatNumber(cashReturn) : '—' }}
                </span>
              </div>
              <div class="sd-info-row">
                <span class="sd-label">持仓市值</span>
                <span class="sd-value sd-highlight">
                  {{ marketValue != null ? '¥' + formatNumber(marketValue) : '—' }}
                </span>
              </div>
            </div>
          </section>
        </div>

        <div class="sd-right">
          <section class="sd-card">
            <div class="sd-card-header">
              <h3 class="sd-card-title">交易趋势</h3>
              <div class="sd-range-toggle">
                <button
                  v-for="r in ranges"
                  :key="r.value"
                  type="button"
                  :class="['sd-range-btn', currentRange === r.value && 'active']"
                  :disabled="klineLoading"
                  @click.stop="handleRangeChange(r.value)"
                >
                  {{ r.label }}
                </button>
              </div>
            </div>
            <div ref="priceChartRef" class="sd-chart-placeholder sd-chart-echarts">
              <p v-if="klineLoading">加载交易趋势中...</p>
            </div>
          </section>
          <section class="sd-card">
            <h3 class="sd-card-title">交易活跃度热力图</h3>
            <div ref="heatmapChartRef" class="sd-chart-placeholder sd-chart-placeholder-light sd-chart-echarts">
              <p v-if="klineLoading">加载活跃度数据中...</p>
            </div>
          </section>
        </div>
      </div>
      <div class="sd-footer">
        <button type="button" class="sd-close-btn" @click="handleClose">关闭</button>
      </div>
    </div>
  </el-dialog>

  <el-dialog
    v-model="chipExplainVisible"
    title="筹码计算明细"
    width="900px"
    append-to-body
    destroy-on-close
    class="sd-chip-ledger-dialog"
  >
    <div class="sd-chip-dialog-scroll">
      <div class="sd-chip-algo-block">
        <div class="sd-chip-algo-item">
          <span class="sd-chip-algo-name">FIFO 持仓均价</span>
          <p class="sd-chip-algo-desc">FIFO均价 = 先进先出结转后的持仓总成本 ÷ 期末持股。</p>
        </div>
        <div class="sd-chip-algo-item">
          <span class="sd-chip-algo-name">浮动盈亏</span>
          <p class="sd-chip-algo-desc">浮动盈亏 =（本期计价价 − FIFO均价）× 期末持股。</p>
        </div>
        <div class="sd-chip-algo-item">
          <span class="sd-chip-algo-name">现金收益</span>
          <p class="sd-chip-algo-desc">现金收益 = 自首期起累计（卖出收回 − 买入付出）。</p>
        </div>
      </div>
      <el-skeleton v-if="chipLedgerLoading" :rows="4" animated />
      <template v-else>
        <el-empty v-if="!chipLedgerRows.length" description="暂无交易明细数据，请先计算交易明细" />
        <div v-else class="sd-chip-table-inner">
          <table class="sd-chip-ledger-table">
          <thead>
            <tr>
              <th>名册日期</th>
              <th>本期变动</th>
              <th>成交价</th>
              <th>期末持股</th>
              <th>FIFO均价</th>
              <th>浮动盈亏</th>
              <th>现金收益</th>
              <th v-if="chipLedgerShowAccountCol">账户</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in chipLedgerRows" :key="idx + '-' + (row.batch_date || '') + '-' + (row.account_code || '')">
              <td>{{ row.batch_date || '—' }}</td>
              <td :class="ledgerDirectionClass(row)">{{ ledgerChangeText(row) }}</td>
              <td :class="ledgerDirectionClass(row)">{{ formatLedgerPrice(row.txn_price) }}</td>
              <td>{{ formatLedgerInt(row.curr_hold_vol) }}</td>
              <td>{{ formatLedgerPrice(row.fifo_cost_avg) }}</td>
              <td :class="ledgerPnlClass(row.fifo_pnl)">{{ formatLedgerMoneySigned(row.fifo_pnl) }}</td>
              <td :class="ledgerPnlClass(row.cash_yield)">{{ formatLedgerMoneySigned(row.cash_yield) }}</td>
              <td v-if="chipLedgerShowAccountCol">{{ shortAccount(row.account_code) }}</td>
            </tr>
          </tbody>
          </table>
        </div>
      </template>
    </div>
  </el-dialog>

  <!-- 无关联时的关联核心股东弹窗 -->
  <el-dialog
    v-model="directConfirmVisible"
    :title="directConfirmDialogTitle"
    width="460px"
    append-to-body
    :lock-scroll="false"
    @closed="onDirectConfirmDialogClosed"
  >
    <div v-if="props.holderName">
      <p v-if="!directEditMode" style="margin:0 0 12px 0">将 <strong>{{ props.holderName }}</strong> 写入核心股东控制列表：</p>
      <p v-else style="margin:0 0 12px 0">可更换挂靠的核心股东或调整关系类型，保存后立即生效。</p>
      <el-form-item label="核心股东" style="margin-bottom:12px">
        <el-select v-model="directConfirmCoreId" placeholder="请选择核心股东" filterable style="width:100%">
          <el-option v-for="c in directConfirmCoreShareholders" :key="c.id" :label="c.name" :value="c.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="控制类型" style="margin-bottom:0">
        <el-select v-model="directConfirmRelationType" style="width:100%">
          <el-option label="账户控制" value="direct" />
          <el-option label="一致行动" value="consistent" />
          <el-option label="间接控制" value="indirect" />
          <el-option label="仅关联" value="associate" />
        </el-select>
      </el-form-item>
    </div>
    <template #footer>
      <div class="sd-direct-footer-btns">
        <el-button @click="directConfirmVisible = false">取消</el-button>
        <el-button type="primary" :loading="directConfirmLoading" @click="submitDirectConfirm">
          {{ directEditMode ? '保存' : '确认' }}
        </el-button>
      </div>
      <el-button
        v-if="directEditMode"
        class="sd-direct-delete-btn"
        type="danger"
        plain
        :loading="directRelationDeleting"
        @click="onDeleteCoreRelation"
      >
        删除关联关系
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as echarts from 'echarts'
import axios from 'axios'

function formatNumber(n) {
  if (n == null || isNaN(n)) return '—'
  return Math.round(n).toLocaleString('zh-CN')
}

function openChipExplain() {
  chipExplainVisible.value = true
  void loadChipLedger()
}

async function loadChipLedger() {
  const name = props.holderName?.trim()
  if (!name) {
    chipLedgerRows.value = []
    return
  }
  chipLedgerLoading.value = true
  chipLedgerRows.value = []
  try {
    const ac = (props.rawShareholderId || accountCode.value || '').trim()
    const params = { holder_name: name }
    if (ac) params.account_code = ac
    const res = await axios.get('/api/shareholder/modal_chip_ledger/', {
      params,
      withCredentials: true,
    })
    const body = res.data
    if (body?.status !== 'ok') {
      ElMessage.error(body?.message || '加载明细失败')
      return
    }
    chipLedgerRows.value = body.data?.rows || []
  } catch (e) {
    ElMessage.error('加载明细失败')
  } finally {
    chipLedgerLoading.value = false
  }
}

function ledgerChangeText(row) {
  const d = String(row.direction || '').trim()
  const v = Number(row.txn_volume) || 0
  if (!d || d === '无交易') return v ? `无交易（${v.toLocaleString('zh-CN')} 股）` : '无交易'
  if (d === '买入') return `买入 ${v.toLocaleString('zh-CN')} 股`
  if (d === '卖出') return `卖出 ${v.toLocaleString('zh-CN')} 股`
  return `${d} ${v.toLocaleString('zh-CN')} 股`
}

function shortAccount(ac) {
  const s = String(ac || '').trim()
  if (!s) return '—'
  return s.length > 8 ? `…${s.slice(-4)}` : s
}

function formatLedgerPrice(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `¥${n.toFixed(2)}`
}

function formatLedgerInt(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return Math.round(n).toLocaleString('zh-CN')
}

function formatLedgerMoneySigned(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(Math.round(n)).toLocaleString('zh-CN')
  if (n > 0) return `+¥${abs}`
  if (n < 0) return `-¥${abs}`
  return '¥0'
}

function ledgerDirectionClass(row) {
  const d = String(row.direction || '').trim()
  if (d === '买入') return 'sd-chip-rise'
  if (d === '卖出') return 'sd-chip-fall'
  return ''
}

/** A 股习惯：红涨绿跌 — 正额红、负额绿 */
function ledgerPnlClass(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n === 0) return ''
  return n > 0 ? 'sd-chip-pnl-pos' : 'sd-chip-pnl-neg'
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: false,
  },
  holderName: {
    type: String,
    default: '',
  },
  linkType: {
    type: String,
    default: null,
  },
  controlTypeLabel: {
    type: String,
    default: '',
  },
  suspiciousRow: {
    type: Object,
    default: null,
  },
  coreName: {
    type: String,
    default: '',
  },
  rawShareholderId: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'update:visible', 'close', 'linked'])

/** 与 el-dialog 直接绑定；避免 v-model 绑 computed 时与 Element Plus 关闭不同步导致关不掉 */
const innerOpen = ref(false)
/** 关闭或重新打开时递增，丢弃仍在进行的异步加载，避免 destroy 后仍 patch DOM 报 __vnode null */
const dialogLoadGeneration = ref(0)

function staleDialog(gen) {
  return !innerOpen.value || (gen != null && gen !== dialogLoadGeneration.value)
}

watch(
  () => !!(props.modelValue || props.visible),
  (open) => {
    innerOpen.value = open
  },
  { immediate: true },
)

watch(innerOpen, (open, oldOpen) => {
  // 仅「由开变关」时同步父级，避免首帧 oldOpen 为 undefined 时误 emit
  if (oldOpen === true && open === false) {
    directConfirmVisible.value = false
    chipExplainVisible.value = false
    disposeCharts()
    dialogLoadGeneration.value += 1
    emit('update:modelValue', false)
    emit('update:visible', false)
    emit('close')
  }
})

/** 点 X / ESC 时先于离场动画执行：先停掉 ECharts，减轻 patch 阶段 __vnode 报错导致关不干净 */
function handleMainBeforeClose(done) {
  directConfirmVisible.value = false
  disposeCharts()
  done()
}

function onDialogClosed() {
  disposeCharts()
  directConfirmVisible.value = false
}

const title = computed(() => props.holderName ? `股东详情：${props.holderName}` : '股东详情')
const initials = computed(() => (props.holderName || '—').slice(0, 1))
const displayCoreName = computed(() => (props.suspiciousRow?.core_name || props.coreName || '').trim())

const chipLedgerShowAccountCol = computed(() => {
  const rows = chipLedgerRows.value || []
  const set = new Set(rows.map((r) => String(r.account_code || '').trim()).filter(Boolean))
  return set.size > 1
})

const ranges = [
  { value: '3m', label: '3个月' },
  { value: '6m', label: '6个月' },
  { value: '1y', label: '1年' },
  { value: '3y', label: '3年' },
  { value: 'all', label: '全部' },
]

const currentRange = ref('1y')
const accountCode = ref('')
const contactAddress = ref('')
const contactPhone = ref('')
const firstAppear = ref('')
const lastAppear = ref('')
const holdPeriods = ref(0)
const tradePeriods = ref(0)
const holdSharesWan = ref(null)
const fifoCost = ref(null)
const fifoPnl = ref(null)
const cashReturn = ref(null)
const marketValue = ref(null)

const chipExplainVisible = ref(false)
const chipLedgerLoading = ref(false)
const chipLedgerRows = ref([])

const priceChartRef = ref(null)
const heatmapChartRef = ref(null)
let priceChart = null
let heatmapChart = null

const klineLoading = ref(false)
const klineDates = ref([])
const klinePrice = ref([])
const klineSeries = ref([])

function disposeCharts() {
  if (priceChart) {
    priceChart.dispose()
    priceChart = null
  }
  if (heatmapChart) {
    heatmapChart.dispose()
    heatmapChart = null
  }
}

async function fetchContactInfo() {
  const name = props.holderName?.trim()
  if (!name) return
  try {
    const res = await axios.get('/api/unique_shareholder/list/', {
      params: { keyword: name, page: 1, page_size: 500 },
    })
    if (res.data?.status === 'ok' && Array.isArray(res.data.data) && res.data.data.length) {
      const targetAccount = props.rawShareholderId?.trim()
      let row
      if (targetAccount) {
        row = res.data.data.find((r) => {
          const a = (r.securities_account_no || '').trim()
          const b = (r.account_code || '').trim()
          const rid = (r.raw_shareholder_id || '').trim()
          return a === targetAccount || b === targetAccount || rid === targetAccount
        })
      }
      if (!row) row = res.data.data[0]
      contactAddress.value = (row.contact_address || '').trim() || ''
      contactPhone.value = (row.contact_phone || '').trim() || ''
      accountCode.value =
        (row.securities_account_no || row.account_code || row.raw_shareholder_id || '').trim() || ''
      firstAppear.value = row.first_appear_time || ''
      lastAppear.value = row.last_appear_time || ''
      holdPeriods.value = row.hold_period_count || 0
      // 当前持股数：见 fetchKline(true) 末尾 applyLatestBatchHoldFromKline（最新一期名册）
    }
  } catch (e) {
    // 静默
  }
}

const localLinkType = ref(null)
const localCoreName = ref('')
const localRelationType = ref('')

const hasControlLink = computed(() => {
  const coreNameVal = localCoreName.value || props.coreName
  return !!coreNameVal
})

/** 与移动端一致：已关联核心时显示蓝字+控制类型，避免 status 为空时误判为未关联 */
const showControlHeader = computed(
  () =>
    props.linkType === 'control' ||
    localLinkType.value === 'control' ||
    (hasControlLink.value && !props.linkType),
)
const showSuspiciousHeader = computed(
  () =>
    !showControlHeader.value &&
    (props.linkType === 'suspicious' || localLinkType.value === 'suspicious'),
)

const ctrlTypeLabel = computed(() => {
  const t = localRelationType.value
  const map = { direct: '账户控制', consistent: '一致行动', indirect: '间接控制', associate: '仅关联' }
  return map[t] || '账户控制'
})

const directConfirmCoreId = ref('')
const directConfirmRelationType = ref('direct')
const directConfirmVisible = ref(false)
const directConfirmCoreShareholders = ref([])
const directConfirmLoading = ref(false)
const directRelationDeleting = ref(false)
const directEditMode = ref(false)
const pushLoading = ref(false)
/** 与移动端一致：用于修改/删除 PUT、DELETE */
const cachedRelationRow = ref(null)

const directConfirmDialogTitle = computed(() =>
  directEditMode.value ? '修改关联关系' : '关联核心股东',
)

const canEditRelation = computed(
  () =>
    !!(cachedRelationRow.value?.relation_id && cachedRelationRow.value?.raw_shareholder_id),
)

function applyCoreRelationRow(row) {
  cachedRelationRow.value = null
  if (!row) return
  const coreNameVal = (row.core_name || '').trim()
  if (!coreNameVal) return
  const st = String(row.status ?? '')
    .trim()
    .toLowerCase()
  if (st === 'inactive' || st === 'deleted' || st === 'disabled') return
  localCoreName.value = coreNameVal
  localRelationType.value = (row.relation_type || 'direct').trim() || 'direct'
  if (st === 'pending') {
    localLinkType.value = 'suspicious'
  } else {
    localLinkType.value = 'control'
  }
  const rid = String(row.relation_id || '').trim()
  const rawSid = String(row.raw_shareholder_id || '').trim()
  if (rid && rawSid) {
    cachedRelationRow.value = {
      relation_id: rid,
      core_shareholder_id: String(row.core_shareholder_id || '').trim(),
      raw_shareholder_id: rawSid,
      relation_type: (row.relation_type || 'direct').trim() || 'direct',
    }
  }
}

function pickRelationRows(rows, acc, coreHint) {
  let list = Array.isArray(rows) ? [...rows] : []
  const a = (acc || '').trim()
  if (a && list.length > 1) {
    const byAcc = list.find((r) => {
      const rid = (r.raw_shareholder_id || '').trim()
      const x = (r.securities_account_no || '').trim()
      const y = (r.account_code || '').trim()
      return rid === a || x === a || y === a
    })
    if (byAcc) list = [byAcc]
  }
  const hint = (coreHint || '').trim()
  if (hint && list.length > 1) {
    const byCore = list.find((r) => (r.core_name || '').trim() === hint)
    if (byCore) list = [byCore]
  }
  return list
}

async function fetchCoreRelation() {
  const acc = (props.rawShareholderId || '').trim()
  const name = props.holderName?.trim()
  if (!acc && !name) {
    cachedRelationRow.value = null
    return
  }
  const coreHint = (props.coreName || '').trim()
  cachedRelationRow.value = null
  try {
    let rows = []
    if (acc) {
      const resAcc = await axios.get('/api/core_relation/', { params: { raw_keyword: acc } })
      if (resAcc.data?.status === 'ok' && Array.isArray(resAcc.data.data)) rows = resAcc.data.data
    }
    if (!rows.length && name) {
      const resName = await axios.get('/api/core_relation/', { params: { raw_keyword: name } })
      if (resName.data?.status === 'ok' && Array.isArray(resName.data.data)) rows = resName.data.data
    }
    if (!rows.length) return
    const picked = pickRelationRows(rows, acc, coreHint)
    const row = picked[0]
    if (row) applyCoreRelationRow(row)
  } catch (e) {
    // 静默
  }
}

function onAssocPillClick() {
  if (!canEditRelation.value) return
  void openEditRelationModal()
}

async function openEditRelationModal() {
  if (!canEditRelation.value) return
  directEditMode.value = true
  await fetchDirectConfirmCoreShareholders()
  directConfirmCoreId.value = cachedRelationRow.value.core_shareholder_id || ''
  directConfirmRelationType.value = cachedRelationRow.value.relation_type || 'direct'
  const id = directConfirmCoreId.value
  const label = (localCoreName.value || props.coreName || '').trim()
  if (id && !directConfirmCoreShareholders.value.some((c) => String(c.id) === String(id))) {
    directConfirmCoreShareholders.value = [{ id, name: label || id }, ...directConfirmCoreShareholders.value]
  }
  directConfirmVisible.value = true
}

async function fetchDirectConfirmCoreShareholders() {
  try {
    const res = await axios.get('/api/penetration/core-shareholders/')
    if (res.data?.status === 'ok' && res.data?.data) {
      directConfirmCoreShareholders.value = res.data.data.map(c => ({ id: c.core_id, name: c.name }))
    } else {
      directConfirmCoreShareholders.value = []
    }
  } catch (e) {
    directConfirmCoreShareholders.value = []
  }
}

function onDirectConfirmDialogClosed() {
  directEditMode.value = false
}

async function openDirectConfirmModal() {
  directEditMode.value = false
  directConfirmCoreId.value = ''
  directConfirmRelationType.value = 'direct'
  await fetchDirectConfirmCoreShareholders()
  directConfirmVisible.value = true
}

async function onDeleteCoreRelation() {
  const rid = cachedRelationRow.value?.relation_id
  if (!rid) {
    ElMessage.warning('缺少关系标识')
    return
  }
  try {
    await ElMessageBox.confirm(
      '确定从核心股东控制列表中删除该关联？删除后不可恢复。',
      '删除关联关系',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  directRelationDeleting.value = true
  try {
    const res = await axios.delete(`/api/core_relation/delete/${rid}/`, { withCredentials: true })
    if (res.data?.status === 'ok') {
      ElMessage.success(res.data?.message || '已删除')
      directConfirmVisible.value = false
      directEditMode.value = false
      cachedRelationRow.value = null
      localCoreName.value = ''
      localRelationType.value = ''
      localLinkType.value = null
      await fetchCoreRelation()
      emit('linked')
    } else {
      ElMessage.error(res.data?.message || '删除失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '删除失败')
  } finally {
    directRelationDeleting.value = false
  }
}

async function submitDirectConfirm() {
  const rel = directConfirmRelationType.value
  if (!rel) return
  if (!directConfirmCoreId.value) {
    ElMessage.warning('请选择核心股东')
    return
  }
  directConfirmLoading.value = true
  try {
    if (directEditMode.value) {
      const rid = cachedRelationRow.value?.relation_id
      const rawId = cachedRelationRow.value?.raw_shareholder_id
      if (!rid || !rawId) {
        ElMessage.warning('缺少关联网信息')
        return
      }
      const res = await axios.put(
        `/api/core_relation/update/${rid}/`,
        {
          core_shareholder_id: String(directConfirmCoreId.value).trim(),
          raw_shareholder_id: rawId,
          relation_type: rel,
          status: 'active',
        },
        { withCredentials: true },
      )
      if (res.data?.status === 'ok') {
        ElMessage.success(res.data?.message || '已保存')
        directConfirmVisible.value = false
        directEditMode.value = false
        const picked = directConfirmCoreShareholders.value.find(
          (c) => String(c.id) === String(directConfirmCoreId.value),
        )
        if (picked?.name) {
          localCoreName.value = picked.name
        }
        localRelationType.value = rel
        await fetchCoreRelation()
        emit('linked')
      } else {
        ElMessage.error(res.data?.message || '保存失败')
      }
      return
    }
    const res = await axios.post('/api/suspicious/direct_confirm/', {
      holder_name: props.holderName,
      relation_type: rel,
      core_id: directConfirmCoreId.value || undefined,
    })
    if (res.data?.status === 'ok') {
      ElMessage.success(res.data?.message || '已写入核心股东控制列表')
      directConfirmVisible.value = false
      const picked = directConfirmCoreShareholders.value.find(
        (c) => c.id === directConfirmCoreId.value,
      )
      if (picked?.name) {
        localCoreName.value = picked.name
        localRelationType.value = rel
        localLinkType.value = 'control'
      }
      await fetchCoreRelation()
      emit('linked')
    } else {
      ElMessage.error(res.data?.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '请求失败')
  } finally {
    directConfirmLoading.value = false
  }
}

async function doPushToAssistant() {
  pushLoading.value = true
  try {
    const name = props.holderName?.trim()
    const res = await axios.get('/api/suspicious/list/', { params: { holder_name: name, status: 'draft', page: 1, page_size: 5 } })
    const list = res.data?.data || []
    const draft = list.find(r => (r.holder_name || '').trim() === name)
    if (draft?.suspicious_id) {
      await axios.post('/api/suspicious/push/', { suspicious_ids: [draft.suspicious_id] })
      ElMessage.success('已推送至助理')
    } else {
      ElMessage.info('该股东暂无待推送的疑似关联记录')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '推送失败')
  } finally {
    pushLoading.value = false
  }
}

onMounted(() => {
  fetchDirectConfirmCoreShareholders()
})

watch(
  innerOpen,
  async (v) => {
    if (v && (props.holderName?.trim() || props.rawShareholderId?.trim())) {
      dialogLoadGeneration.value += 1
      const gen = dialogLoadGeneration.value
      currentRange.value = '1y'
      tradePeriods.value = 0
      fifoCost.value = null
      fifoPnl.value = null
      cashReturn.value = null
      holdSharesWan.value = null
      localLinkType.value = null
      localCoreName.value = ''
      localRelationType.value = ''
      await fetchCoreRelation()
      if (staleDialog(gen)) return
      await fetchContactInfo()
      if (staleDialog(gen)) return
      await fetchKline(true, gen)
    }
  },
  { immediate: true },
)

watch(() => props.rawShareholderId, async (newId, oldId) => {
  if (!innerOpen.value || !newId || newId === oldId) return
  dialogLoadGeneration.value += 1
  const gen = dialogLoadGeneration.value
  holdSharesWan.value = null
  await fetchCoreRelation()
  if (staleDialog(gen)) return
  await fetchContactInfo()
  if (staleDialog(gen)) return
  await fetchKline(true, gen)
})

watch(() => props.holderName, async (newName, oldName) => {
  if (!innerOpen.value || !newName || newName === oldName) return
  dialogLoadGeneration.value += 1
  const gen = dialogLoadGeneration.value
  currentRange.value = '1y'
  tradePeriods.value = 0
  fifoCost.value = null
  fifoPnl.value = null
  cashReturn.value = null
  marketValue.value = null
  holdSharesWan.value = null
  await fetchCoreRelation()
  if (staleDialog(gen)) return
  await fetchContactInfo()
  if (staleDialog(gen)) return
  await fetchKline(true, gen)
})

function handleClose() {
  innerOpen.value = false
}

function handleRangeChange(val) {
  if (!innerOpen.value) return
  currentRange.value = val
  disposeCharts()
  fetchKline()
}

async function handleRefresh() {
  if (!innerOpen.value) return
  dialogLoadGeneration.value += 1
  const gen = dialogLoadGeneration.value
  currentRange.value = '1y'
  klineLoading.value = true
  try {
    await fetchCoreRelation()
    if (staleDialog(gen)) return
    await fetchContactInfo()
    if (staleDialog(gen)) return
    await fetchKline(true, gen)
  } finally {
    klineLoading.value = false
  }
}

function getKlineDateRange() {
  const end = new Date()
  const start = new Date()
  switch (currentRange.value) {
    case '3m':
      start.setMonth(start.getMonth() - 3)
      break
    case '6m':
      start.setMonth(start.getMonth() - 6)
      break
    case '1y':
      start.setFullYear(start.getFullYear() - 1)
      break
    case '3y':
      start.setFullYear(start.getFullYear() - 3)
      break
    case 'all':
    default:
      return {}
  }
  const fmt = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  return { start_date: fmt(start), end_date: fmt(end) }
}

async function fetchKline(fetchAll = false, gen = null) {
  if (!innerOpen.value) return
  if (staleDialog(gen)) return
  const name = props.holderName?.trim()
  if (!name) return
  klineLoading.value = true
  try {
    const params = { holders: name, limit_batches: 999 }
    if (props.rawShareholderId) params.account_code = props.rawShareholderId
    if (!fetchAll) {
      const range = getKlineDateRange()
      if (range.start_date) {
        params.start_date = range.start_date
        params.end_date = range.end_date
      }
    }
    const res = await axios.get('/api/transaction_detail/kline/', { params })
    if (staleDialog(gen)) return
    if (res.data?.status !== 'ok') return
    if (!innerOpen.value) return

    const data = res.data.data || {}
    klineDates.value = data.dates || []
    klinePrice.value = data.price || []
    klineSeries.value = data.series || []

    if (fetchAll) {
      computeTradePeriods()
      await fetchChipFromLastAppear(gen)
      if (staleDialog(gen)) return
      applyLatestBatchHoldFromKline()
    }

    await nextTick()
    if (staleDialog(gen)) return

    if (priceChart) {
      priceChart.dispose()
      priceChart = null
    }
    if (heatmapChart) {
      heatmapChart.dispose()
      heatmapChart = null
    }

    renderCharts()
  } catch (e) {
    console.error('load kline failed', e)
  } finally {
    klineLoading.value = false
  }
}

function computeTradePeriods() {
  const dates = klineDates.value || []
  const s0 = (klineSeries.value || [])[0]
  const deltas = (s0 && s0.deltas) || []
  if (!dates.length) return
  
  let count = 0
  dates.forEach((d, i) => {
    const delta = deltas[i]
    if (delta != null && delta !== 0) count++
  })
  tradePeriods.value = count
}

/** 全市场最新一期名册末格持股（万股）；该期未在名册则为空。与移动端一致。 */
function applyLatestBatchHoldFromKline() {
  const s0 = (klineSeries.value || [])[0]
  const holds = (s0 && s0.holds) || []
  if (!holds.length) {
    holdSharesWan.value = null
    return
  }
  const last = holds[holds.length - 1]
  if (last === null || last === undefined) {
    holdSharesWan.value = null
    return
  }
  const n = Number(last)
  holdSharesWan.value = Number.isFinite(n) ? n : null
}

function applyChipRow(r) {
  const num = (x) => {
    if (x == null || x === '') return null
    const v = Number(x)
    return Number.isFinite(v) ? v : null
  }
  fifoCost.value = num(r.fifo_cost_avg)
  fifoPnl.value = num(r.fifo_pnl)
  cashReturn.value = num(r.cash_yield)
  marketValue.value = num(r.hold_market_val)
}

function clearChipMetrics() {
  fifoCost.value = null
  fifoPnl.value = null
  cashReturn.value = null
  marketValue.value = null
}

/** 筹码：先按「全市场最新一期名册」取点；该期无此人则改用档案里的「最近出现」日期再取（已清仓/不在最新期仍可看最后一期筹码）。 */
async function fetchChipFromLastAppear(gen = null) {
  if (staleDialog(gen)) return
  const name = props.holderName?.trim()
  if (!name) return
  const acct = props.rawShareholderId?.trim()
  const tryPoint = async (dateStr) => {
    if (staleDialog(gen)) return false
    if (!dateStr) return false
    const params = { holder: name, date: dateStr }
    if (acct) params.account_code = acct
    const res = await axios.get('/api/transaction_detail/point/', { params })
    if (staleDialog(gen)) return false
    const rows = res.data?.data
    if (res.data?.status === 'ok' && Array.isArray(rows) && rows.length) {
      applyChipRow(rows[0])
      return true
    }
    return false
  }
  try {
    clearChipMetrics()
    const dates = klineDates.value || []
    const lastBatch = dates.length ? String(dates[dates.length - 1]).slice(0, 10) : ''
    if (await tryPoint(lastBatch)) return
    if (staleDialog(gen)) return
    const fromProfile = String(lastAppear.value || '').trim().slice(0, 10)
    if (fromProfile && fromProfile !== lastBatch) {
      if (await tryPoint(fromProfile)) return
    }
  } catch (e) {
    // 静默
  }
}

function renderCharts() {
  if (!innerOpen.value) return
  if (!priceChartRef.value || !heatmapChartRef.value) return
  try {
    renderPriceChart()
    renderHeatmap()
  } catch (e) {
    console.error('render charts failed', e)
  }
}

function computeHoldSummary() {
  const dates = klineDates.value || []
  const s0 = (klineSeries.value || [])[0]
  const holds = (s0 && s0.holds) || []
  if (!dates.length || !holds.length) {
    firstAppear.value = ''
    lastAppear.value = ''
    holdPeriods.value = 0
    holdSharesWan.value = null
    return
  }
  const positiveIdx = []
  holds.forEach((v, idx) => {
    if (typeof v === 'number' && !Number.isNaN(v) && v > 0) positiveIdx.push(idx)
  })
  if (!positiveIdx.length) {
    firstAppear.value = ''
    lastAppear.value = ''
    holdPeriods.value = 0
    holdSharesWan.value = null
    return
  }
  const firstIdx = positiveIdx[0]
  const lastIdx = positiveIdx[positiveIdx.length - 1]
  firstAppear.value = dates[firstIdx] || ''
  lastAppear.value = dates[lastIdx] || ''
  holdPeriods.value = positiveIdx.length
  holdSharesWan.value = holds[lastIdx] ?? null
}

async function fetchPointDetail() {
  const dates = klineDates.value || []
  const name = props.holderName?.trim()
  if (!dates.length || !name) return
  const lastDate = dates[dates.length - 1]
  try {
    const params = { holder: name, date: lastDate }
    if (props.rawShareholderId) params.account_code = props.rawShareholderId
    const res = await axios.get('/api/transaction_detail/point/', { params })
    if (res.data?.status !== 'ok') return
    const rows = res.data.data || []
    if (!rows.length) return

    let totalVol = 0
    let costSum = 0
    let pnlSum = 0
    let cashSum = 0
    let mvSum = 0
    const accounts = new Set()
    rows.forEach(r => {
      const vol = Number(r.curr_hold_vol || 0)
      const cost = Number(r.fifo_cost_avg || 0)
      const pnl = Number(r.fifo_pnl || 0)
      const cash = Number(r.cash_yield || 0)
      const mv = Number(r.hold_market_val || 0)
      totalVol += vol
      costSum += vol * cost
      pnlSum += pnl
      cashSum += cash
      mvSum += mv
      if (r.account_code) accounts.add(r.account_code)
    })
    accountCode.value = Array.from(accounts).join(' / ')
    fifoCost.value = totalVol > 0 ? costSum / totalVol : null
    fifoPnl.value = pnlSum
    cashReturn.value = cashSum
    marketValue.value = mvSum
  } catch (e) {
    console.error('load point detail failed', e)
  }
}

function renderPriceChart() {
  const el = priceChartRef.value
  if (!el) return
  if (!priceChart) {
    priceChart = echarts.init(el)
  }
  const dates = klineDates.value || []
  const price = klinePrice.value || []
  const series = []

  series.push({
    name: '股价',
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 4,
    lineStyle: { width: 2, color: '#0f766e' },
    itemStyle: { color: '#0f766e' },
    data: price,
    areaStyle: { color: 'rgba(15,118,110,0.06)' },
  })

  const s0 = (klineSeries.value || [])[0]
  const deltas = (s0 && s0.deltas) || []
  const holderLabel = (s0 && (s0.holder || s0.account)) || props.holderName || '股东'

  const buyPoints = []
  const sellPoints = []
  dates.forEach((d, i) => {
    const p = price[i]
    const delta = deltas[i] ?? 0
    if (p === null || p === undefined) return
    if (!delta) return
    const base = { value: [d, p], delta, holder: holderLabel }
    if (delta > 0) buyPoints.push(base)
    else if (delta < 0) sellPoints.push(base)
  })

  if (buyPoints.length) {
    series.push({
      name: holderLabel + ' 净买入',
      type: 'scatter',
      symbol: 'triangle',
      symbolSize: 10,
      itemStyle: { color: '#16a34a', borderColor: '#ffffff', borderWidth: 2 },
      z: 5,
      data: buyPoints,
    })
  }

  if (sellPoints.length) {
    series.push({
      name: holderLabel + ' 净卖出',
      type: 'scatter',
      symbol: 'triangle',
      symbolSize: 10,
      symbolRotate: 180,
      itemStyle: { color: '#dc2626', borderColor: '#ffffff', borderWidth: 2 },
      z: 5,
      data: sellPoints,
    })
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        if (!params || !params.length) return ''
        const first = params[0]
        const date = first.axisValue
        const scatterPoint = params.find(p => p.data && p.data.delta !== undefined)
        if (!scatterPoint) {
          const priceVal = typeof first.value === 'number' ? first.value : (first.value && first.value[1])
          return `${date}<br/>${first.marker} 股价：${priceVal}元`
        }
        const raw = scatterPoint.data || {}
        const delta = raw.delta || 0
        const priceVal = raw.value ? raw.value[1] : (typeof first.value === 'number' ? first.value : null)
        const volStr = delta > 0 ? `买入 ${Math.abs(delta).toFixed(2)}万股` : `卖出 ${Math.abs(delta).toFixed(2)}万股`
        return `${date}<br/>${first.marker} 股价：${priceVal}元<br/>${scatterPoint.marker} ${volStr}`
      }
    },
    legend: series.length ? { data: series.map(s => s.name), top: 0 } : { show: false },
    grid: { left: '5%', right: '5%', top: 40, bottom: 20, containLabel: true },
    xAxis: { type: 'category', data: dates, axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#e5e7eb' } } },
    yAxis: { type: 'value', name: '价格（元）', axisLabel: { formatter: '{value}', color: '#64748b' }, splitLine: { lineStyle: { color: '#e5e7eb' } } },
    series,
  }

  priceChart.setOption(option, true)
}

function renderHeatmap() {
  const el = heatmapChartRef.value
  if (!el) return
  if (!heatmapChart) {
    heatmapChart = echarts.init(el)
  }
  const dates = klineDates.value || []
  const s0 = (klineSeries.value || [])[0]
  const deltas = (s0 && s0.deltas) || []

  const data = []
  const maxIntensity = 4
  dates.forEach((d, idx) => {
    const delta = Math.abs(deltas[idx] ?? 0)
    let intensity = 0
    if (delta > 0) {
      if (delta < 1) intensity = 1
      else if (delta < 3) intensity = 2
      else if (delta < 10) intensity = 3
      else intensity = 4
    }
    data.push([idx, 0, intensity])
  })

  const option = {
    tooltip: { position: 'top' },
    grid: { left: '5%', right: '16%', top: 20, bottom: 20, containLabel: true },
    xAxis: { type: 'category', data: dates, axisLabel: { color: '#64748b', formatter: v => v?.slice(5) || '' }, splitArea: { show: false } },
    yAxis: { type: 'category', data: ['活跃度'], axisLabel: { color: '#64748b' }, splitArea: { show: false } },
    visualMap: { min: 0, max: maxIntensity, calculable: false, orient: 'vertical', right: 4, top: 20, bottom: 20, inRange: { color: ['#f1f5f9', '#bfdbfe', '#60a5fa', '#2563eb', '#1e40af'] }, text: ['高', '低'], show: true },
    series: [{ name: '交易活跃度', type: 'heatmap', data, label: { show: false }, emphasis: { itemStyle: { borderColor: '#3b82f6', borderWidth: 1 } } }],
  }

  heatmapChart.setOption(option, true)
}
</script>

<style>
.sd-dialog .el-dialog__body {
  padding: 12px 16px;
}
</style>

<style scoped>
.sd-root {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.sd-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #0f172a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
}

.sd-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sd-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sd-name {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.sd-core-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sd-core-pill--clickable {
  cursor: pointer;
}

.sd-core-pill--clickable:hover {
  background: #dbeafe;
}

.sd-direct-footer-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.sd-direct-delete-btn {
  width: 100%;
  margin-top: 12px;
}

.ctrl-type-tag {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 999px;
  font-weight: 600;
}
.ctrl-type-direct, .ctrl-type-consistent, .ctrl-type-indirect {
  background: #dbeafe;
  color: #1d4ed8;
}
.ctrl-type-associate {
  background: #ede9fe;
  color: #7c3aed;
}

.sd-no-link-btns {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.sd-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.sd-refresh-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  color: #374151;
  z-index: 100;
  margin-left: auto;
  line-height: 1;
}

.sd-refresh-btn:hover {
  background: #f3f4f6;
}

.sd-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 12px;
}

.sd-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px 14px;
}

.sd-left .sd-card {
  min-height: auto;
}

.sd-right .sd-card {
  min-height: auto;
}

.sd-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sd-range-toggle {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 3px;
  border-radius: 999px;
}

.sd-card-gradient {
  background: linear-gradient(135deg, #eff6ff, #eef2ff);
}

.sd-card-title {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.sd-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.sd-info-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sd-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}

.sd-info-row--help .sd-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sd-help-q {
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.sd-help-q:hover {
  border-color: #94a3b8;
  color: #0f172a;
}

.sd-chip-dialog-scroll {
  max-height: 72vh;
  overflow-x: auto;
  overflow-y: auto;
}

.sd-chip-algo-block {
  margin-bottom: 14px;
  padding-right: 4px;
}

.sd-chip-algo-item {
  padding: 10px 0;
}

.sd-chip-algo-item + .sd-chip-algo-item {
  border-top: 1px solid #e2e8f0;
}

.sd-chip-algo-name {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.sd-chip-algo-desc {
  margin: 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.55;
  text-align: justify;
}

.sd-chip-table-inner {
  min-width: min-content;
}

.sd-chip-ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sd-chip-ledger-table th,
.sd-chip-ledger-table td {
  border: 1px solid #e2e8f0;
  padding: 8px 6px;
  text-align: center;
  vertical-align: middle;
}

.sd-chip-ledger-table th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
}

.sd-chip-ledger-table td:nth-child(2) {
  max-width: 200px;
  white-space: normal;
  word-break: break-word;
}

.sd-chip-ledger-table .sd-chip-rise {
  color: #dc2626;
  font-weight: 700;
}

.sd-chip-ledger-table .sd-chip-fall {
  color: #059669;
  font-weight: 700;
}

.sd-chip-ledger-table .sd-chip-pnl-pos {
  color: #dc2626;
  font-weight: 700;
}

.sd-chip-ledger-table .sd-chip-pnl-neg {
  color: #059669;
  font-weight: 700;
}

.sd-label {
  color: #64748b;
}

.sd-value {
  color: #0f172a;
  font-weight: 500;
}

.sd-muted {
  color: #94a3b8;
}

.sd-highlight {
  font-weight: 800;
  color: #1d4ed8;
}

.sd-up {
  font-weight: 700;
  color: #dc2626;
}

.sd-down {
  font-weight: 700;
  color: #059669;
}

.sd-suspicious-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.sd-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sd-range-toggle {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 3px;
  border-radius: 999px;
}

.sd-range-btn {
  border: none;
  background: transparent;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  cursor: pointer;
  color: #64748b;
}

.sd-range-btn.active {
  background: #0f172a;
  color: #ffffff;
}

.sd-range-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sd-chart-echarts {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.sd-chart-echarts p {
  margin: auto;
}

.sd-chart-placeholder {
  height: 220px;
  border-radius: 8px;
  background: #0f172a;
  color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.sd-chart-placeholder-light {
  background: #f8fafc;
  color: #64748b;
  height: 160px;
}

.sd-footer {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  padding-top: 6px;
  border-top: 1px solid #e2e8f0;
  margin-top: 4px;
}

.sd-close-btn {
  padding: 6px 20px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  font-size: 13px;
}

.sd-close-btn:hover {
  background: #f3f4f6;
}
</style>
